import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Heart, MessageCircle, Plus, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CURRENT_USER } from "@/data/listings";

export function AppHeader() {
  const location = useLocation();

  const navItems = [
    { path: "/", label: "Discover", icon: ShoppingBag },
    { path: "/saved", label: "Saved", icon: Heart },
    { path: "/messages", label: "Messages", icon: MessageCircle },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/95 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--blush-deep)" }}>
            <span className="text-white text-xs font-bold">CL</span>
          </div>
          <span className="font-bold text-lg tracking-tight" style={{ color: "var(--rose)" }}>
            Closet<span className="font-light" style={{ color: "var(--foreground)" }}>Loop</span>
          </span>
        </Link>

        {/* Location pill */}
        <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-border/60 bg-muted/40 cursor-pointer hover:bg-muted transition-colors">
          <MapPin size={13} style={{ color: "var(--rose)" }} />
          <span className="text-muted-foreground text-xs font-medium">{CURRENT_USER.location}</span>
        </div>

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map(({ path, label, icon: Icon }) => (
            <Link key={path} to={path}>
              <Button
                variant="ghost"
                size="sm"
                className={`flex items-center gap-2 text-sm rounded-full px-4 transition-all ${
                  location.pathname === path
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon size={15} />
                {label}
              </Button>
            </Link>
          ))}
        </nav>

        {/* CTA + Avatar */}
        <div className="flex items-center gap-3">
          <Link to="/sell">
            <Button
              size="sm"
              className="rounded-full px-4 gap-1.5 text-sm font-medium shadow-sm"
              style={{ background: "var(--rose)", color: "white" }}
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Sell a Dress</span>
            </Button>
          </Link>
          <Link to="/profile">
            <img
              src={CURRENT_USER.avatar}
              alt={CURRENT_USER.name}
              className="w-8 h-8 rounded-full object-cover border-2 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ borderColor: "var(--blush)" }}
            />
          </Link>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-background/95 backdrop-blur-md">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map(({ path, label, icon: Icon }) => (
            <Link key={path} to={path} className="flex flex-col items-center gap-0.5 py-2 px-4">
              <Icon
                size={20}
                style={{ color: location.pathname === path ? "var(--rose)" : "var(--muted-foreground)" }}
              />
              <span
                className="text-xs"
                style={{ color: location.pathname === path ? "var(--rose)" : "var(--muted-foreground)" }}
              >
                {label}
              </span>
            </Link>
          ))}
          <Link to="/sell" className="flex flex-col items-center gap-0.5 py-2 px-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center -mt-6 shadow-lg" style={{ background: "var(--rose)" }}>
              <Plus size={20} color="white" />
            </div>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-0.5 py-2 px-4">
            <img src={CURRENT_USER.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
            <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>Profile</span>
          </Link>
        </div>
      </div>
    </header>
  );
}
